import GridContainer from '@navi/web-ui/lib/layouts/Grid/GridContainer/GridContainer';
import GridRow from '@navi/web-ui/lib/layouts/Grid/GridRow/GridRow';
import { Tag, Typography } from '@navi/web-ui/lib/primitives';
import styles from './AmountdueCard.module.scss';
import { formatAmount } from 'src/utils/commonUtils';
import cx from 'classnames';
import EnachCard from './dc-97/EnachCard';
import {
  HumanReminderType,
  IHumanReminderDues,
  MandateStatus,
  RepaymentFailureDetails,
  setHumanReminderCustomerDetailsLoading,
  UpcomingDueDetails,
  WAIVER_REQUEST_TYPE
} from 'src/reducers/commonSlice';
import { COLOR_CODING, ColorCoding, DAYS_PAST_DUE } from './dc-97/dc97Constant';
import { calculateDateDifferenceInDays } from '../../utils/DateHelper';
import { Tooltip, TooltipContent, TooltipTrigger } from '@cp/components/TooltipV2/TooltipV2';
import InfoIconOutlined from '@cp/assets/images/icons/InfoIconOutlined';
import HourglassIconV2 from '@cp/assets/icons/HourglassIconV2';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import { RefreshIcon } from '@navi/web-ui/lib/icons';
import { getCustomerDetails } from './dc-97/HumanReminderAction';
import { setShowMainScreen } from '@cp/src/reducers/humanReminderSlice';
import { noop } from '@navi/web-ui/lib/utils/common';

interface AmountDueCardProps {
  totalAmount: number;
  emiDueDate: string;
  emiAmount: number;
  emiDaysRemaining: number;
  emiPenaltyCharge: string | number;
  emiIncreasePenaltyChargeDate: string;
  emiIncreasePenalChargeAmount: number;
  emiAmountWithIncrementalPenaltyCharge: number;
  mandateStatus: MandateStatus;
  repaymentFailureDetails: RepaymentFailureDetails;
  reminderType: HumanReminderType;
  upcomingDueDetails?: UpcomingDueDetails;
  totalUnpaidDueAmount: number;
  totalEmiAmount: number;
  penaltyApplied: number;
  nextIncrementAmount: number;
  paidAmount: number;
  heldPenaltyCharge: number;
  holdEligibleDues: IHumanReminderDues;
  nextDayDues: IHumanReminderDues;
}

const getDpdColor = (
  dpd: number,
  type: COLOR_CODING,
  reminderType: HumanReminderType = HumanReminderType.POST_DUE
): string => {
  if (reminderType === HumanReminderType.PRE_DUE) {
    return ColorCoding.PRE_DUE.color[type];
  }
  return Object.values(ColorCoding).reduce((acc, curr) => {
    return curr.min <= dpd && curr.max >= dpd ? curr.color[type] : acc;
  }, 'var(--floral-white)');
};

const getEmiDays = (dpd: number) => {
  return dpd === 0
    ? DAYS_PAST_DUE.DUE_TODAY
    : dpd > 1
    ? `${dpd} ${DAYS_PAST_DUE.DUE_DAYS}`
    : `${dpd} ${DAYS_PAST_DUE.DUE_DAY}`;
};

const AmountDueCard = (props: AmountDueCardProps) => {
  const {
    emiDueDate,
    emiDaysRemaining,
    emiPenaltyCharge,
    emiIncreasePenaltyChargeDate,
    mandateStatus,
    repaymentFailureDetails,
    reminderType,
    upcomingDueDetails,
    emiAmountWithIncrementalPenaltyCharge,
    totalUnpaidDueAmount,
    totalEmiAmount,
    penaltyApplied,
    nextIncrementAmount,
    paidAmount,
    heldPenaltyCharge,
    holdEligibleDues,
    nextDayDues
  } = props;
  const dispatch = useDispatch();
  const callId = useSelector((store: RootState) => store?.humanReminder?.callId);
  const upcomingDueDateDiff = calculateDateDifferenceInDays(
    new Date(),
    new Date(upcomingDueDetails?.emiDueDate)
  );
  const upcomingDueDays = upcomingDueDateDiff > 1 ? DAYS_PAST_DUE.DUE_DAYS : DAYS_PAST_DUE.DUE_DAY;
  const isReminderTypePreDue = reminderType === HumanReminderType.PRE_DUE;

  const mandateInProgress = useSelector(
    (store: RootState) =>
      store?.common?.humanReminderCustomerDetails?.humanReminderResponse?.loanDetails
        ?.mandateInProgress
  );

  const isMandateStatusSuccess = mandateStatus === MandateStatus.SUCCESS;
  const isMandateStatusNull = mandateStatus === undefined || mandateStatus === null;
  const enachCardText = repaymentFailureDetails?.reason || '';
  const isRepaymentFailureDetailsNull =
    repaymentFailureDetails === undefined || repaymentFailureDetails === null;

  const handleDataRefresh = () => {
    dispatch(setHumanReminderCustomerDetailsLoading(true));
    dispatch(getCustomerDetails(callId, noop));
  };

  return (
    <GridContainer className={styles.emiDueContainer}>
      <GridContainer className={styles.totalEmiDueContainer}>
        <div
          className={styles.topChip}
          style={{
            background: getDpdColor(emiDaysRemaining, COLOR_CODING.TOP_CHIP_BG_COLOR, reminderType)
          }}
        >
          <span
            className={styles.remaningDays}
            style={{
              color: getDpdColor(emiDaysRemaining, COLOR_CODING.TOP_CHIP_COLOR, reminderType)
            }}
          >
            {reminderType === HumanReminderType.POST_DUE ? (
              getEmiDays(emiDaysRemaining)
            ) : reminderType === HumanReminderType.PRE_DUE ? (
              <>
                <span className={styles.topChipText}>Due after </span>
                <b>
                  {upcomingDueDateDiff} {upcomingDueDays}
                </b>
              </>
            ) : (
              '--'
            )}
          </span>
          {emiDaysRemaining && reminderType === HumanReminderType.POST_DUE ? (
            <span className={styles.topChipText}>since Due Date ({emiDueDate})</span>
          ) : null}
        </div>
        <GridRow className={styles.justify_btw}>
          <div className="flex row-auto items-center gap-2">
            <Typography variant="h3" className={styles.headingText}>
              Total Overdue
            </Typography>
            <RefreshIcon onClick={handleDataRefresh} className="cursor-pointer" />
          </div>
          <Typography
            variant="h3"
            className={styles.headingValue}
            color={getDpdColor(emiDaysRemaining, COLOR_CODING.TOTAL_AMOUNT_COLOR, reminderType)}
          >
            {totalUnpaidDueAmount ? formatAmount(totalUnpaidDueAmount) : '--'}
          </Typography>
        </GridRow>
      </GridContainer>
      <GridContainer
        style={{ background: getDpdColor(emiDaysRemaining, COLOR_CODING.CARD_COLOR, reminderType) }}
        className={cx(styles.emiPenaltyContainer, styles.emiPenaltyChargeBorder)}
      >
        {reminderType === HumanReminderType.POST_DUE ? (
          <>
            <GridRow className={cx(styles.emiPenaltyRow, styles.justify_btw)}>
              <Typography variant="p3" className={styles.emiPenaltyText}>
                EMI Amount
              </Typography>
              <Typography variant="p3" className={cx(styles.fw700, styles.emiPenaltyValue)}>
                {formatAmount(totalEmiAmount)}
              </Typography>
            </GridRow>
            <GridRow className={cx(styles.emiPenaltyCharge, styles.justify_btw)}>
              <Typography variant="p3" className={styles.emiPenaltyText}>
                EMI Penalty
              </Typography>
              <Typography
                variant="p3"
                className={cx(styles.fw700, styles.emiPenaltyValue)}
                color={typeof emiPenaltyCharge !== 'number' ? 'var(--blue-base)' : ''}
              >
                {typeof emiPenaltyCharge === 'number'
                  ? formatAmount(emiPenaltyCharge)
                  : emiPenaltyCharge}
              </Typography>
            </GridRow>
            {(holdEligibleDues || nextDayDues) && (
              <GridRow className={cx(styles.emiPenaltyCharge, styles.justify_btw)}>
                <Typography
                  variant="p3"
                  className={cx(styles.emiPenaltyText, 'flex gap-[10px] items-center')}
                >
                  Penalty Held
                  {nextDayDues ? (
                    <div className={cx('flex', 'gap-[8px]', 'items-center')}>
                      <Tag
                        color="blue"
                        startAdornment={<HourglassIconV2 />}
                        label={`Till 11:59 PM today`}
                        size="sm"
                        variant="transparent"
                      />
                      <Tooltip placement="top" hiddenPadding={0} hideStrategy="referenceHidden">
                        <TooltipTrigger tooltipTriggerClassName="tooltipTriggerWrapper">
                          <InfoIconOutlined fillColor="var(--blue-base)" />
                        </TooltipTrigger>
                        <TooltipContent className={styles.tooltipWrapper}>
                          <div className="flex justify-between gap-[20px]">
                            <Typography
                              color="var(--text-primary)"
                              className="text-[13px]"
                              variant="p4"
                            >
                              Total Overdue (Tomorrow)
                            </Typography>
                            <Typography
                              color="var(--text-primary)"
                              className="text-[13px]"
                              variant="p4"
                            >
                              {formatAmount(nextDayDues?.totalUnpaidDueAmount?.amount || 0)}
                            </Typography>
                          </div>
                          <div className="flex justify-between gap-[20px]">
                            <Typography
                              color="var(--text-primary)"
                              className="text-[13px]"
                              variant="p4"
                            >
                              EMI Penalty (Tomorrow)
                            </Typography>
                            <Typography
                              color="var(--text-primary)"
                              className="text-[13px]"
                              variant="p4"
                            >
                              {formatAmount(nextDayDues?.penaltyApplied?.amount || 0)}
                            </Typography>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  ) : (
                    <>
                      <Tooltip placement="top" hiddenPadding={0} hideStrategy="referenceHidden">
                        <TooltipTrigger tooltipTriggerClassName="tooltipTriggerWrapper">
                          <Tag
                            labelClass="cursor-default"
                            label={`View hold eligibility`}
                            size="sm"
                            endAdornment={
                              <InfoIconOutlined fillColor="var(--navi-color-gray-c3)" />
                            }
                          />
                        </TooltipTrigger>
                        <TooltipContent
                          arrowClassName={styles.arrow}
                          className={cx(styles.tooltipWrapper, 'ml-[42px]')}
                        >
                          <div className="flex justify-between gap-[20px]">
                            <Typography
                              color="var(--text-primary)"
                              className="text-[13px]"
                              variant="p4"
                            >
                              Total overdue after{' '}
                              {holdEligibleDues?.waiverRequestType === WAIVER_REQUEST_TYPE.WAIVER
                                ? `waiver`
                                : 'hold'}
                            </Typography>
                            <Typography
                              color="var(--text-primary)"
                              className="text-[13px]"
                              variant="p4"
                            >
                              {formatAmount(holdEligibleDues?.totalUnpaidDueAmount?.amount || 0)}
                            </Typography>
                          </div>
                          <div className="flex justify-between gap-[20px]">
                            <Typography
                              color="var(--text-primary)"
                              className="text-[13px]"
                              variant="p4"
                            >
                              {holdEligibleDues?.waiverRequestType === WAIVER_REQUEST_TYPE.WAIVER
                                ? `Amount to be waived (100%)`
                                : 'Penalty to be held (50%)'}
                            </Typography>
                            <Typography
                              color="var(--text-primary)"
                              className="text-[13px]"
                              variant="p4"
                            >
                              {formatAmount(holdEligibleDues?.heldPenaltyCharge?.amount || 0)}
                            </Typography>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </>
                  )}
                </Typography>
                <Typography variant="p3" className={cx(styles.fw700, styles.emiPenaltyValue)}>
                  {`${heldPenaltyCharge > 0 ? '-' : ''} ${formatAmount(heldPenaltyCharge)}`}
                </Typography>
              </GridRow>
            )}
            <GridRow className={cx(styles.emiPenaltyCharge, styles.justify_btw)}>
              <Typography variant="p3" className={styles.emiPenaltyText}>
                Paid Amount
              </Typography>
              <Typography
                variant="p3"
                className={cx(styles.fw700, styles.emiPenaltyValue)}
                color="var(--green-base)"
              >
                - {formatAmount(paidAmount)}
              </Typography>
            </GridRow>
            <GridRow
              style={{ border: '1px dashed #CACED5' }}
              className={styles.emitPenaltyRowBorder}
            >
              {' '}
            </GridRow>
            <GridRow className={cx(styles.emiPenaltyRow, styles.justify_btw)}>
              <GridRow className={styles.justify_btw} style={{ margin: '0px', padding: '0px' }}>
                <Typography
                  variant="p3"
                  className={cx(styles.emiPenaltyText, 'flex items-center gap-[10px]')}
                >
                  Upcoming Overdue
                  <Tag
                    color="blue"
                    label={emiIncreasePenaltyChargeDate ? `On ${emiIncreasePenaltyChargeDate}` : ``}
                    size="sm"
                    variant="transparent"
                  />
                </Typography>
              </GridRow>
              <GridRow style={{ margin: '0px', padding: '0px' }}>
                <Typography variant="p3" className={styles.emiPenaltyValue}>
                  {formatAmount(emiAmountWithIncrementalPenaltyCharge)}
                </Typography>
              </GridRow>
            </GridRow>
            <GridRow className={cx(styles.emiPenaltyRow, styles.justify_btw)}>
              <Typography variant="p3" className={styles.emiPenaltyText}>
                Upcoming EMI Penalty
              </Typography>
              <Typography variant="p3" className={styles.emiPenaltyValue}>
                {formatAmount(nextIncrementAmount)}
              </Typography>
            </GridRow>
          </>
        ) : reminderType === HumanReminderType.PRE_DUE ? (
          <>
            <GridRow className={cx(styles.emiPenaltyRow, styles.justify_btw)}>
              <Typography variant="p3" className={styles.emiPenaltyText}>
                EMI Amount
              </Typography>
              <Typography variant="p3" className={styles.emiPenaltyValue}>
                {formatAmount(totalEmiAmount)}
              </Typography>
            </GridRow>
            <GridRow className={cx(styles.emiPenaltyRow, styles.justify_btw)}>
              <Typography variant="p3" className={styles.emiPenaltyText}>
                EMI Penalty
              </Typography>
              <Typography variant="p3" className={styles.emiPenaltyValue}>
                {formatAmount(penaltyApplied)}
              </Typography>
            </GridRow>
            <GridRow className={cx(styles.emiPenaltyRow, styles.justify_btw)}>
              <Typography variant="p3" className={styles.emiPenaltyText}>
                Paid Amount
              </Typography>
              <Typography
                variant="p3"
                className={cx(styles.emiPenaltyValue, 'font-medium')}
                color="var(--green-base)"
              >
                - {formatAmount(paidAmount)}
              </Typography>
            </GridRow>
            <GridRow
              style={{ border: '1px dashed #CACED5' }}
              className={styles.emitPenaltyRowBorder}
            >
              {' '}
            </GridRow>
            <GridRow className={cx(styles.emiPenaltyRow, styles.justify_btw)}>
              <Typography variant="p3" className={cx(styles.emiPenaltyText, 'font-medium')}>
                Upcoming Overdue
                {emiIncreasePenaltyChargeDate && (
                  <Tag
                    color="blue"
                    label={`On ${emiIncreasePenaltyChargeDate}`}
                    size="sm"
                    variant="transparent"
                  />
                )}
              </Typography>
              <Typography variant="p3" className={styles.emiPenaltyValue}>
                {formatAmount(emiAmountWithIncrementalPenaltyCharge)}
              </Typography>
            </GridRow>
            <GridRow className={cx(styles.emiPenaltyRow, styles.justify_btw)}>
              <Typography variant="p3" className={styles.emiPenaltyText}>
                Upcoming EMI Penalty
              </Typography>
              <Typography variant="p3" className={styles.emiPenaltyValue}>
                {formatAmount(nextIncrementAmount)}
              </Typography>
            </GridRow>
          </>
        ) : (
          <></>
        )}
      </GridContainer>
      {!isMandateStatusNull && (
        <EnachCard
          date={repaymentFailureDetails?.nextEnachPresentationDate || ''}
          isMandateStatusSuccess={isMandateStatusSuccess}
          enachCardText={enachCardText}
          enachCardDate={repaymentFailureDetails?.orderCreationTimestamp || ''}
          mandateInProgress={mandateInProgress || false}
          isReminderTypePreDue={isReminderTypePreDue}
          isRepaymentFailureDetailsNull={isRepaymentFailureDetailsNull}
        />
      )}
    </GridContainer>
  );
};

export default AmountDueCard;
